import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import logo from '@/assets/sterling_logo.png';
import s from './Landing.module.css';

/**
 * The public marketing page, shown at `/` to anyone who isn't signed in.
 *
 * Ported from the "Sterling Landing" design export. The export used inline
 * styles with literal colours; everything here goes through the shared tokens
 * so the burgundy primary stays in one place.
 */

const STATS = [
  { value: '97%', label: 'auto-categorized correctly' },
  { value: '$1,240', label: 'avg. waste found per year' },
  { value: '6 min', label: 'to connect your accounts' },
];

/** Net-worth sparkline from the hero card. Static by design — it's a still of the product. */
const TREND_LINE =
  'M0 84 L36 76 L72 79 L108 62 L144 66 L180 52 L216 56 L252 40 L288 44 L324 30 L360 24 L400 12';

function Wordmark({ compact }: { compact?: boolean }) {
  return (
    <div className={s.brand}>
      <img src={logo} alt="" className={compact ? s.logoSm : s.logo} />
      <div>
        <div className={compact ? s.wordmarkSm : s.wordmark}>Sterling</div>
        <div className={s.byline}>by cardinale</div>
      </div>
    </div>
  );
}

export default function Landing() {
  // Signed in, the page keeps its shape and only swaps the calls to action —
  // "create an account" becomes a way into the app. While the cold-start
  // refresh is still in flight this reads as signed out, which is the right
  // guess for a visitor; a signed-in one who beats it to the click lands on
  // sign-in and is forwarded straight through to the app anyway.
  const { user } = useAuth();
  const signedIn = Boolean(user);
  const ctaTo = signedIn ? '/app' : '/signin?mode=register';

  return (
    <div className={s.page}>
      <header className={s.header}>
        <Wordmark />
        <nav className={s.nav} aria-label="Sections">
          <a href="#features">Product</a>
          <a href="#advisor">Advisor</a>
          <a href="#security">Security</a>
        </nav>
        <div className={s.headerActions}>
          {signedIn ? null : (
            <Link to="/signin" className={s.quietLink}>
              Sign in
            </Link>
          )}
          <Link to={ctaTo} className={s.cta}>
            {signedIn ? 'Dashboard' : 'Get started'}
          </Link>
        </div>
      </header>

      <section className={s.hero}>
        <div>
          <div className={s.eyebrowPill}>
            <span className={s.pulse} />
            your money, on autopilot
          </div>
          <h1 className={s.h1}>A finance manager with an advisor built in.</h1>
          <p className={s.lede}>
            Sterling tracks every expense and income, categorizes it automatically, and shows you the
            analytics that matter. Its AI advisor tells you how much to save, what to cut, and where
            to invest — inside guardrails you set.
          </p>
          <div className={s.ctaRow}>
            <Link to={ctaTo} className={s.ctaLg}>
              {signedIn ? 'Open your dashboard' : 'Start free'}
            </Link>
            <a href="#features" className={s.ctaGhost}>
              See how it works →
            </a>
          </div>
          <div className={s.stats}>
            {STATS.map((stat) => (
              <div key={stat.label}>
                <div className={s.statValue}>{stat.value}</div>
                <div className={s.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={s.heroArt}>
          <div className={s.netWorthCard}>
            <div className="eyebrow">net worth</div>
            <div className={s.netWorth}>$128,400</div>
            <div className={s.netWorthMeta}>
              <span className={s.deltaChip}>↗ +4.2%</span>
              <span className={s.deltaNote}>+$5,693 this month</span>
            </div>
            <svg
              width="100%"
              height="110"
              viewBox="0 0 400 110"
              preserveAspectRatio="none"
              className={s.spark}
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="landing-trend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.32" />
                  <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`${TREND_LINE} L400 110 L0 110 Z`} fill="url(#landing-trend)" />
              <path
                d={TREND_LINE}
                fill="none"
                stroke="var(--accent)"
                strokeWidth="2.4"
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                className={s.sparkLine}
              />
            </svg>
          </div>

          <div className={s.advisorCard}>
            <div className={s.advisorLabel}>advisor</div>
            <div className={s.advisorBody}>
              $6,400 is idle in checking. Sweeping it to your 4.35% savings earns{' '}
              <span className={s.advisorFigure}>+$232/yr</span> — your buffer stays intact.
            </div>
            <div className={s.advisorActions}>
              <span className={s.advisorPrimary}>Sweep now</span>
              <span className={s.advisorDismiss}>Dismiss</span>
            </div>
          </div>

          <div className={s.savingsCard}>
            <div className="eyebrow">savings rate</div>
            <div className={s.savingsValue}>56%</div>
          </div>
        </div>
      </section>

      <section id="features" className={s.features}>
        <div className="eyebrow">what it does</div>
        <h2 className={s.h2}>Everything a spreadsheet does, none of the spreadsheet.</h2>
        <div className={s.featureGrid}>
          <article className={s.featureCard}>
            <div className={s.categoryBar} aria-hidden="true">
              <span style={{ width: '38%', background: 'oklch(0.55 0.17 10)' }} />
              <span style={{ width: '22%', background: 'oklch(0.68 0.13 35)' }} />
              <span style={{ width: '16%', background: 'oklch(0.78 0.09 85)' }} />
              <span style={{ width: '24%', background: 'oklch(0.62 0.08 350)' }} />
            </div>
            <h3 className={s.featureTitle}>Automatic categorization</h3>
            <p className={s.featureBody}>
              Every expense and income lands in the right bucket the moment it clears. Correct one and
              it learns.
            </p>
          </article>

          <article className={s.featureCard}>
            <svg width="80" height="26" viewBox="0 0 80 26" className={s.bars} aria-hidden="true">
              <rect x="0" y="14" width="10" height="12" rx="2" fill="var(--accent-hi)" opacity="0.55" />
              <rect x="14" y="8" width="10" height="18" rx="2" fill="var(--accent-hi)" opacity="0.75" />
              <rect x="28" y="12" width="10" height="14" rx="2" fill="var(--accent-hi)" opacity="0.65" />
              <rect x="42" y="3" width="10" height="23" rx="2" fill="var(--accent-hi)" />
              <rect x="56" y="9" width="10" height="17" rx="2" fill="var(--accent-hi)" opacity="0.85" />
              <rect x="70" y="6" width="10" height="20" rx="2" fill="var(--accent-2)" />
            </svg>
            <h3 className={s.featureTitle}>Analytics that answer</h3>
            <p className={s.featureBody}>
              Cashflow, budgets, heatmaps, merchant trends, savings rate — every chart tied to a
              decision.
            </p>
          </article>

          <article id="advisor" className={`${s.featureCard} ${s.featureCardAccent}`}>
            <div className={s.advisorTag}>
              <span className={s.advisorDot} />
              <span>ai advisor</span>
            </div>
            <h3 className={s.featureTitle}>Advice from your data</h3>
            <p className={s.featureBody}>
              How much to save, which subscriptions to cut, when to rebalance — grounded in your
              actual accounts, not generic tips.
            </p>
          </article>

          <article id="security" className={s.featureCard}>
            <div className={s.guardrails}>
              <span>observe</span>
              <span className={s.guardrailOn}>suggest</span>
              <span>act</span>
            </div>
            <h3 className={s.featureTitle}>You set the guardrails</h3>
            <p className={s.featureBody}>
              Choose how autonomous the AI is — from read-only to acting within hard limits you
              define. Every move is logged.
            </p>
          </article>
        </div>
      </section>

      <section className={s.closing}>
        <div className={s.closingCard}>
          <h2 className={s.h2Center}>
            {signedIn ? 'Pick up where you left off.' : 'Stop tracking. Start deciding.'}
          </h2>
          <p className={s.closingSub}>
            {signedIn
              ? 'Your accounts, analytics and advisor are where you left them.'
              : 'Free and self-hosted. No card required.'}
          </p>
          <Link to={ctaTo} className={s.ctaLg}>
            {signedIn ? 'Open your dashboard' : 'Create your account'}
          </Link>
        </div>

        <footer className={s.footer}>
          <Wordmark compact />
          <span className={s.copyright}>© 2026 Cardinale Group</span>
          <div className={s.footerLinks}>
            <a href="#security">Security</a>
            {signedIn ? null : <Link to="/signin">Sign in</Link>}
            <Link to={ctaTo}>{signedIn ? 'Dashboard' : 'Get started'}</Link>
          </div>
        </footer>
      </section>
    </div>
  );
}
